/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import {
	EffectCoverflow,
	Pagination,
	Navigation,
	Autoplay,
} from "swiper/modules";
import DotGrid from "./DotGrid";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGsapScrollParallax } from "@/hooks/useGsapScrollParallax";

import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import "swiper/css/navigation";

const IndustriesWeServe: React.FC = () => {
	const { t } = useTranslation();
	const sectionRef = useRef<HTMLElement | null>(null);
	const swiperRef = useRef<SwiperType | null>(null);

	useGsapScrollParallax(sectionRef, { y: -100, opacity: 0.9, scale: 0.98 });

	const data = {
		hero: {
			title: t("industriesServe.title"),
			description: t("industriesServe.description"),
		},
	};

	const industries = [
		{
			name: t("industriesServe.items.powerGeneration.name"),
			href: "/industries/power-generation",
			image: "/images/carouselimgs/nuclear-plant.jpg",
			desc: t("industriesServe.items.powerGeneration.desc"),
		},
		{
			name: t("industriesServe.items.oilGas.name"),
			href: "/industries/oil-gas",
			image: "/images/industry/oil-gas.webp",
			desc: t("industriesServe.items.oilGas.desc"),
		},
		{
			name: t("industriesServe.items.renewableEnergy.name"),
			href: "/industries/renewable-energy",
			image: "/images/industry/renewables-energy.webp",
			desc: t("industriesServe.items.renewableEnergy.desc"),
		},
		{
			name: t("industriesServe.items.dataCenter.name"),
			href: "/industries/data-center",
			image: "/images/freeDownloads/data-centers.jpg",
			desc: t("industriesServe.items.dataCenter.desc"),
		},
		{
			name: t("industriesServe.items.waterTreatment.name"),
			href: "/industries/water-treatment",
			image: "/images/industry/water-treatment.webp",
			desc: t("industriesServe.items.waterTreatment.desc"),
		},
		{
			name: t("industriesServe.items.steelMetallurgy.name"),
			href: "/industries/steel-metallurgy",
			image: "/images/industry/steel-metallury.webp",
			desc: t("industriesServe.items.steelMetallurgy.desc"),
		},
		{
			name: t("industriesServe.items.chemicalProcessing.name"),
			href: "/industries/chemical-fertilizer",
			image: "/images/industry/chemical.webp",
			desc: t("industriesServe.items.chemicalProcessing.desc"),
		},
		{
			name: t("industriesServe.items.plasticInjectionMolding.name"),
			href: "/industries/plastic-injection-molding",
			image: "/images/industry/plasticinjection.webp",
			desc: t("industriesServe.items.plasticInjectionMolding.desc"),
		},
		{
			name: t("industriesServe.items.marineOffshore.name"),
			href: "/industries/marine-offshore",
			image: "/images/industry/marine.webp",
			desc: t("industriesServe.items.marineOffshore.desc"),
		},
		{
			name: t("industriesServe.items.processIndustries.name"),
			href: "/industries/process-industries",
			image: "/images/industry/ProcessIndustries.webp",
			desc: t("industriesServe.items.processIndustries.desc"),
		},
		{
			name: t("industriesServe.items.hvacSystems.name"),
			href: "/industries/hvac-systems",
			image: "/images/industry/hvac.jpg",
			desc: t("industriesServe.items.hvacSystems.desc"),
		},
	];

	return (
		<section
			ref={sectionRef}
			className="relative w-full overflow-hidden bg-[#3b0092] px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
			{/* Background Grid */}
			<div className="absolute inset-0">
				<DotGrid
					dotSize={4}
					gap={15}
					baseColor="#2c016c"
					activeColor="#f09a38"
					proximity={100}
					shockRadius={200}
					shockStrength={3}
					resistance={750}
					returnDuration={1.5}
				/>
			</div>

			{/* Header */}
			<motion.div
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true }}
				transition={{ duration: 0.7 }}
				className="relative z-10 mx-auto mb-10 max-w-5xl text-center">
				<h2 className="mb-4 text-2xl font-semibold text-white sm:mb-6 sm:text-4xl lg:text-6xl">
					{data.hero.title}
				</h2>

				<p className="mx-auto max-w-3xl text-sm text-gray-300 sm:text-lg">
					{data.hero.description}
				</p>
			</motion.div>

			{/* Swiper */}
			<Swiper
				onSwiper={(swiper) => (swiperRef.current = swiper)}
				effect="coverflow"
				centeredSlides
				loop
				slidesPerView={3}
				spaceBetween={30}
				autoplay={{
					delay: 2000,
					disableOnInteraction: false,
					pauseOnMouseEnter: true,
				}}
				speed={800}
				coverflowEffect={{
					rotate: 0,
					stretch: 0,
					depth: 150,
					modifier: 2,
					slideShadows: false,
				}}
				breakpoints={{
					320: { slidesPerView: 1 },
					768: { slidesPerView: 2 },
					1024: { slidesPerView: 3 },
				}}
				modules={[EffectCoverflow, Pagination, Navigation, Autoplay]}>
				{industries.map((item, index) => (
					<SwiperSlide key={index}>
						{/* FULL CARD CLICKABLE */}
						<Link
							to={item.href}
							className="block group">
							<div className="relative rounded-2xl overflow-hidden h-80">
								{/* Image */}
								<img
									src={item.image}
									alt={item.name}
									className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
								/>

								{/* Overlay */}
								<div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
								<div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition duration-500" />

								{/* Content — the copy below the heading was hover-only, so on
								    touch devices the cards showed nothing but a name. It is
								    now visible by default and keeps the hover reveal at `lg`. */}
								<div className="absolute bottom-5 left-5 right-5 transition-all duration-500 sm:bottom-6 sm:left-6 sm:right-6 lg:group-hover:bottom-10">
									<h3 className="mb-2 text-xl font-semibold text-white sm:text-2xl">
										{item.name}
									</h3>

									<p className="text-sm text-gray-300 opacity-100 transition duration-500 lg:opacity-0 lg:group-hover:opacity-100">
										{item.desc}
									</p>

									<span className="mt-3 inline-block text-sm text-[#f09a38] opacity-100 transition duration-500 lg:opacity-0 lg:group-hover:opacity-100">
										{t("industriesServe.learnMore")} →
									</span>
								</div>
							</div>
						</Link>
					</SwiperSlide>
				))}
			</Swiper>
		</section>
	);
};

export default IndustriesWeServe;
